import { IRequestAction, progressEpic } from "epics/base-epic";
import { INVENTORY } from "constants/action-types";
import siteConfig from "site-config";
import { stringify } from "querystring";
import { IInventoryMap } from "types/inventory";
import type { IWarehouse } from "types/inventory";
import _ from "lodash";
import fp from "lodash/fp";
import { Observable } from "rxjs";
import { fetchReplenishments } from "actions/inventory";
import { getInventoryRefs } from "helpers/order-refs";
import { updateWorkingOrderState } from "actions/working-order-state";
import { getSessionKey } from "helpers/inventory";

const SKU_CHUNK = 100;

export interface IWarehouseFetchPayload {
    skuIds: string[];
    companyId: string;
    /**
     * Identifier of specific context for which warehouses are requested, can be brandId, orderId, productId+companyId, etc.
     */
    context: string;
}

interface RequestInventoryAction {
    payload: Array<{ warehouse: string; skusByProductId: Record<string, string[]> }>;
    meta: {
        forceNotify?: boolean;
        adjust: boolean;
        companyId: string; // TODO: remove when the data is in Redux
    };
}

/** Initiates replenishments fetching for Working Order. Is called only from Working Order */
export const requestReplenishmentsWorkingOrderEpic = (action$, store) =>
    action$.ofType(INVENTORY.REPLENISHMENTS.REQUEST).mergeMap((action: RequestInventoryAction) => {
        const { inventory, cart } = store.getState();
        const refs = getInventoryRefs(store.getState());
        const session = cart?.result; // order._id. safe to use as `clear cart` creates a new _id

        const sameSession = getSessionKey({ ...refs, session }) === inventory.replenishments.meta?.sessionKey;
        if (action?.meta?.forceNotify && sameSession) {
            const replenishments = _.flatMap(inventory.replenishments.payload, fp.flatMap(fp.identity));
            store.dispatch(updateWorkingOrderState({ replenishments }, action.meta));
        }

        return _.flatMap(action.payload, ({ warehouse, skusByProductId }) =>
            chunkSkus(skusByProductId).map((skus) =>
                fetchReplenishments(warehouse, skus, { ...action.meta, ...refs, session, notifyCart: true })
            )
        );
    });

export const fetchReplenishmentsEpic = progressEpic<IRequestAction, IInventoryMap>(
    INVENTORY.REPLENISHMENTS.FETCH,
    ({ payload }) => ({ method: "GET", url: siteConfig.api + payload.url }),
    { mapper: "mergeMap", concurrency: 10 }
);

export const fetchWarehousesEpic = progressEpic<IWarehouseFetchPayload, { [context: string]: IWarehouse[] }>(
    INVENTORY.WAREHOUSES.FETCH,
    ({ payload }) => ({
        method: "GET",
        url: `${siteConfig.api}/api/v3.0/warehouses?${stringify(
            _.pickBy({
                __company: payload.companyId,
                sku_id: payload.skuIds,
            })
        )}`,
    }),
    {
        dataCustomizer: (response: IWarehouse[], action) => ({
            [action.payload.context]: response,
        }),
        mapper: "mergeMap",
        // when no warehouses found appserver will return 404 status, do not throw
        checkError: (error) =>
            error.status === 404
                ? Observable.of({
                      response: [],
                      status: 200,
                      xhr: error.xhr,
                  })
                : Observable.throw(error),
    }
);

/** Splits provided skus in chunks so that every product's skus lay in the same chunk */
function chunkSkus(skusByProductId: Record<string, string[]>, size: number = SKU_CHUNK) {
    const chunks: string[][] = [];
    _.forEach(skusByProductId, (skus) => {
        if (!chunks.length || _.last(chunks)!.length + skus.length > size) {
            chunks.push([]);
        }
        _.last(chunks)!.push(...skus);
    });
    return chunks;
}
